import { useCallback, useMemo, useRef } from 'react';

/**
 * Guards a parent BottomSheet from closing when nested overlays (pickers, dialogs)
 * are open or in the process of closing. Radix nested Dialog dismiss can propagate
 * to the parent onOpenChange(false) in the same tick as a child closes.
 */
export function useGuardedBottomSheetClose({ onOpen, onClose, childOpen = [] }) {
  const suppressCloseRef = useRef(false);
  const childAnyOpen = childOpen.some(Boolean);

  const handleOpenChange = useCallback(
    (open) => {
      if (open) {
        onOpen?.();
        return;
      }

      if (suppressCloseRef.current || childAnyOpen) {
        return;
      }

      onClose?.();
    },
    [onOpen, onClose, childAnyOpen],
  );

  const blockDismissProps = useMemo(
    () => ({
      onInteractOutside: (event) => {
        if (childAnyOpen) {
          event.preventDefault();
        }
      },
      onEscapeKeyDown: (event) => {
        if (childAnyOpen) {
          event.preventDefault();
        }
      },
    }),
    [childAnyOpen],
  );

  const wrapChildOpenChange = useCallback((setChildOpen) => {
    return (open) => {
      if (!open) {
        suppressCloseRef.current = true;
        requestAnimationFrame(() => {
          suppressCloseRef.current = false;
        });
      }
      setChildOpen(open);
    };
  }, []);

  return { handleOpenChange, blockDismissProps, wrapChildOpenChange };
}
